import { supabase } from "./supabase";
import type { Article, Tag, Highlight, SortKey, LibraryFilter } from "./types";

// ---------- articles ----------

export interface ListOptions {
  filter: LibraryFilter;
  sort: SortKey;
  search?: string;
  tagId?: string;
  limit?: number;
}

export async function listArticles(opts: ListOptions): Promise<Article[]> {
  let q = supabase.from("articles").select("*");

  if (opts.filter === "inbox") q = q.is("archived_at", null);
  else if (opts.filter === "archive") q = q.not("archived_at", "is", null);
  else if (opts.filter === "favorites") q = q.eq("favorite", true);

  if (opts.sort === "saved") q = q.order("saved_at", { ascending: false });
  else if (opts.sort === "published") q = q.order("published_at", { ascending: false, nullsFirst: false });
  else q = q.order("title", { ascending: true });

  if (opts.search) {
    const term = opts.search.trim();
    // Escape LIKE wildcards so user input isn't interpreted as patterns.
    const escaped = term.replace(/[%_\\]/g, (c) => "\\" + c);
    q = q.or(
      `title.ilike.%${escaped}%,author.ilike.%${escaped}%,search_vector.phfts.${encodeURIComponent(term)}`,
    );
  }

  if (opts.tagId) {
    const { data: links } = await supabase
      .from("article_tags")
      .select("article_id")
      .eq("tag_id", opts.tagId);
    const ids = (links ?? [])
      .filter((r): r is { article_id: string } =>
        Boolean(r) && typeof r === "object" && "article_id" in r && typeof r.article_id === "string")
      .map((r) => r.article_id);
    q = q.in("id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"]);
  }

  const { data, error } = await q.limit(opts.limit ?? 200);
  if (error) throw error;
  return (data ?? []) as Article[];
}

export async function getArticle(id: string): Promise<Article | null> {
  const { data, error } = await supabase.from("articles").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return (data ?? null) as Article | null;
}

export async function submitUrl(url: string): Promise<{ id: string; status: string }> {
  // Insert a pending row via RLS (user_id defaults to auth.uid()), then invoke
  // the extract Edge Function which derives user_id from the JWT.
  const { data, error } = await supabase
    .from("articles")
    .insert({ url, normalized_url: normalizeUrlClient(url), extraction_status: "pending" })
    .select("id, extraction_status")
    .single();

  if (error) {
    if (error.code === "23505") {
      // Duplicate inbox URL — return the existing pending/ready row.
      const existing = await supabase
        .from("articles")
        .select("id, extraction_status")
        .eq("normalized_url", normalizeUrlClient(url))
        .is("archived_at", null)
        .maybeSingle();
      if (existing.data) {
        return { id: (existing.data as { id: string }).id, status: (existing.data as { extraction_status: string }).extraction_status };
      }
    }
    throw error;
  }

  const inserted = data as { id: string; extraction_status: string };

  // Fire the Edge Function; the row exists so a failure here is recoverable
  // via the scheduled queue.
  const { data: session } = await supabase.auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/extract`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.session?.access_token ?? ""}`,
    },
    body: JSON.stringify({ article_id: inserted.id }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "extraction_failed" }));
    if (typeof body === "object" && body !== null && "error" in body) {
      return { id: inserted.id, status: `error:${(body as { error: string }).error}` };
    }
    return { id: inserted.id, status: `error:${res.status}` };
  }
  return { id: inserted.id, status: "extracting" };
}

export async function submitHtml(url: string, title: string, html: string): Promise<{ id: string }> {
  // Extension / bookmarklet posts already-rendered HTML for paywalled pages.
  const { data: session } = await supabase.auth.getSession();
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit_html`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.session?.access_token ?? ""}`,
    },
    body: JSON.stringify({ url, title, html }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "submit_failed" }));
    if (typeof body === "object" && body !== null && "error" in body) {
      throw new Error((body as { error: string }).error);
    }
    throw new Error(`submit_failed:${res.status}`);
  }
  const body = (await res.json()) as unknown;
  if (typeof body === "object" && body !== null && "id" in body && typeof (body as { id: unknown }).id === "string") {
    return { id: (body as { id: string }).id };
  }
  throw new Error("submit_failed:malformed_response");
}

export async function updateArticle(id: string, patch: Partial<Article>): Promise<void> {
  const { error } = await supabase.from("articles").update(patch).eq("id", id);
  if (error) throw error;
}

export async function archiveArticle(id: string, archived: boolean): Promise<void> {
  const archived_at = archived ? new Date().toISOString() : null;
  await updateArticle(id, { archived_at });
}

export async function favoriteArticle(id: string, favorite: boolean): Promise<void> {
  await updateArticle(id, { favorite });
}

export async function markRead(id: string, read: boolean): Promise<void> {
  const read_at = read ? new Date().toISOString() : null;
  await updateArticle(id, { read_at });
}

export async function setProgress(id: string, progress: number): Promise<void> {
  await supabase
    .from("articles")
    .update({ reading_progress: Math.max(0, Math.min(1, progress)) })
    .eq("id", id);
}

export async function deleteArticles(ids: string[]): Promise<void> {
  const { error } = await supabase.from("articles").delete().in("id", ids);
  if (error) throw error;
}

export async function bulkArchive(ids: string[], archive: boolean): Promise<void> {
  const archived_at = archive ? new Date().toISOString() : null;
  const { error } = await supabase.from("articles").update({ archived_at }).in("id", ids);
  if (error) throw error;
}

// ---------- tags ----------

export async function listTags(): Promise<Tag[]> {
  const { data, error } = await supabase.from("tags").select("id, user_id, name, created_at").order("name");
  if (error) throw error;
  return (data ?? []) as Tag[];
}

export async function ensureTag(name: string): Promise<Tag> {
  const clean = name.trim();
  if (!clean) throw new Error("Tag name required");
  const { data, error } = await supabase
    .from("tags")
    .upsert({ name: clean }, { onConflict: "user_id,name" })
    .select("id, name")
    .single();
  if (error) throw error;
  return data as Tag;
}

export async function setArticleTags(articleId: string, tagIds: string[]): Promise<void> {
  await supabase.from("article_tags").delete().eq("article_id", articleId);
  if (tagIds.length) {
    const rows = tagIds.map((tag_id) => ({ article_id: articleId, tag_id }));
    const { error } = await supabase.from("article_tags").insert(rows);
    if (error) throw error;
  }
}

export async function addTagsToArticle(articleId: string, names: string[]): Promise<void> {
  const ids: string[] = [];
  for (const n of names) ids.push((await ensureTag(n)).id);
  await setArticleTags(articleId, ids);
}

export async function deleteTag(id: string): Promise<void> {
  const { error } = await supabase.from("tags").delete().eq("id", id);
  if (error) throw error;
}

interface ArticleTagJoin {
  tags: { id: string; name: string } | null;
}

export async function tagsForArticle(articleId: string): Promise<Tag[]> {
  const { data } = await supabase
    .from("article_tags")
    .select("tags:id, name")
    .eq("article_id", articleId);

  // The select clause above doesn't model the join cleanly without generated
  // types; narrow each row defensively.
  const rows = (data ?? []) as unknown[];
  const tags: Tag[] = [];
  for (const r of rows) {
    if (r && typeof r === "object" && "tags" in r) {
      const inner = (r as ArticleTagJoin).tags;
      if (inner && typeof inner === "object" && "id" in inner && "name" in inner) {
        tags.push({ id: inner.id, name: inner.name } as Tag);
      }
    }
  }
  return tags;
}

// ---------- highlights ----------

export async function listHighlights(articleId: string): Promise<Highlight[]> {
  const { data, error } = await supabase
    .from("highlights")
    .select("*")
    .eq("article_id", articleId)
    .order("created_at");
  if (error) throw error;
  return (data ?? []) as Highlight[];
}

interface HighlightJoin extends Highlight {
  article: Article | null;
}

export async function listAllHighlights(): Promise<Array<Highlight & { article: Article }>> {
  const { data, error } = await supabase
    .from("highlights")
    .select("*, article:articles(*)")
    .order("created_at", { ascending: false });
  if (error) throw error;

  const rows = (data ?? []) as unknown[] as HighlightJoin[];
  return rows.filter((r): r is HighlightJoin & { article: Article } => Boolean(r.article));
}

export async function createHighlight(
  h: Omit<Highlight, "id" | "user_id" | "created_at" | "updated_at">,
): Promise<Highlight> {
  const { data, error } = await supabase.from("highlights").insert(h).select("*").single();
  if (error) throw error;
  return data as Highlight;
}

export async function updateHighlight(id: string, patch: Partial<Highlight>): Promise<void> {
  const { error } = await supabase.from("highlights").update(patch).eq("id", id);
  if (error) throw error;
}

export async function deleteHighlight(id: string): Promise<void> {
  const { error } = await supabase.from("highlights").delete().eq("id", id);
  if (error) throw error;
}

// ---------- url normalization (mirrors the SQL normalize_url) ----------

export function normalizeUrlClient(raw: string): string {
  // Stable key for dedup: lowercase, strip scheme + www + trailing slash + fragment.
  return raw
    .replace(/#.*$/, "")
    .replace(/\/+$/, "")
    .replace(/^https?:\/\/(www\.)?/i, "")
    .toLowerCase();
}
